import { createContext, useContext, ReactNode } from 'react'
import { useTRPC, RouterOutputs } from '@/trpc'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'

type User = RouterOutputs['auth']['me']
type Company = User['companies'][number]

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  logout: () => void
  refetch: () => void
  company: Company | null
}

let AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  let trpc = useTRPC()
  let queryClient = useQueryClient()

  let queryOptions = trpc.auth.me.queryOptions()
  let query = useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  let user = (query.data as User | undefined) ?? null
  // @ts-ignore trpc deep type
  let company: Company | null = user?.companies?.find((c: Company) => c.id !== 0) ?? null

  let logoutMutation = useMutation(trpc.auth.logout.mutationOptions({
    onSuccess: () => {
      queryClient.setQueryData(trpc.auth.me.queryKey(), undefined)
      queryClient.clear()
    },
  }))

  let logout = () => logoutMutation.mutate()

  return (
    <AuthContext.Provider value={{
      user,
      isLoading: query.isLoading,
      isAuthenticated: !!user,
      logout,
      refetch: query.refetch,
      company,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  let context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
